import { useCallback, useEffect, useState } from 'react';
import { resellerAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  Loader2, Copy, Check, RefreshCw, Save, Edit3, X, Sparkles,
  ImageIcon, Trash2, Eye, Share2, ClipboardCopy, Wand2, ImagePlus,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const STATUS_COLOR = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  ready: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  failed: 'bg-red-50 text-red-700 border-red-200',
};

export default function ResellerPanel({ product }) {
  const productId = product.id;
  const [pack, setPack] = useState({ description: '', media: [], best_title: '' });
  const [loading, setLoading] = useState(true);
  const [generatingDesc, setGeneratingDesc] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState('');
  const [savingDesc, setSavingDesc] = useState(false);
  const [copyKey, setCopyKey] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerKind, setPickerKind] = useState('hero'); // 'hero' | 'continuation'
  const [pickerParentId, setPickerParentId] = useState(null);
  const [submittingGen, setSubmittingGen] = useState(false);
  const [previewMedia, setPreviewMedia] = useState(null);

  const productPhotos = (product.media || []).filter(
    (m) => m.kind === 'photo' || m.kind === 'image' || !m.kind,
  );

  const load = useCallback(async () => {
    try {
      const res = await resellerAPI.get(productId);
      setPack(res.data);
    } catch (err) {
      console.error('reseller load', err);
      if (!loading) toast.error('Failed to refresh reseller pack');
    } finally {
      setLoading(false);
    }
  }, [productId, loading]);

  useEffect(() => { load(); }, [load]);

  // Poll every 6s while any reseller media is pending.
  useEffect(() => {
    const anyPending = (pack.media || []).some((m) => m.status === 'pending');
    if (!anyPending) return undefined;
    const t = setTimeout(() => load(), 6000);
    return () => clearTimeout(t);
  }, [pack.media, load]);

  const handleCopy = (text, key, msg = 'Copied') => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopyKey(key);
    setTimeout(() => setCopyKey(null), 1500);
    toast.success(msg);
  };

  const handleGenerateDescription = async () => {
    try {
      setGeneratingDesc(true);
      const res = await resellerAPI.generateDescription(productId);
      setPack({ ...pack, description: res.data.description, description_updated_at: res.data.description_updated_at });
      toast.success('Reseller description generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Description generation failed');
    } finally {
      setGeneratingDesc(false);
    }
  };

  const handleSaveDescription = async () => {
    const trimmed = descDraft.trim();
    if (!trimmed) { toast.error('Description cannot be empty'); return; }
    try {
      setSavingDesc(true);
      const res = await resellerAPI.editDescription(productId, trimmed);
      setPack({ ...pack, description: res.data.description, description_updated_at: res.data.description_updated_at });
      setEditingDesc(false);
      toast.success('Saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSavingDesc(false);
    }
  };

  const openPicker = (kind, parentId = null) => {
    setPickerKind(kind);
    setPickerParentId(parentId);
    setPickerOpen(true);
  };

  const handlePickSource = async (sourceMediaId) => {
    try {
      setSubmittingGen(true);
      if (pickerKind === 'hero') {
        const res = await resellerAPI.generateHero(productId, sourceMediaId);
        setPack({ ...pack, media: [...(pack.media || []), res.data.media] });
        toast.success('Hero generation started — takes ~2 min');
      } else {
        const res = await resellerAPI.generateContinuation(productId, sourceMediaId, pickerParentId);
        setPack({ ...pack, media: [...(pack.media || []), res.data.media] });
        toast.success('Continuation generation started — takes ~2 min');
      }
      setPickerOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed to start');
    } finally {
      setSubmittingGen(false);
    }
  };

  const handleDeleteMedia = async (rmId) => {
    try {
      await resellerAPI.deleteMedia(productId, rmId);
      setPack({ ...pack, media: (pack.media || []).filter((m) => m.id !== rmId) });
      toast.success('Removed');
    } catch {
      toast.error('Failed to remove');
    }
  };

  // === Mobile share / copy helpers — same pattern as the Etsy product
  // media tiles, but routed through the reseller blob endpoint.
  const buildShareCaption = () => {
    const url = product.tlj_published_url || product.input_url || '';
    return [pack.best_title || product.best_title || '', url].filter(Boolean).join('\n');
  };

  const handleShareMedia = async (m) => {
    try {
      const blob = await resellerAPI.fetchMediaBlob(productId, m.id);
      const file = new File([blob], m.display_name || `reseller-${m.id}.png`, {
        type: blob.type || m.content_type || 'image/png',
      });
      const payload = { files: [file], title: pack.best_title || 'TheLocalJewel', text: buildShareCaption() };
      if (navigator.canShare && navigator.canShare(payload) && navigator.share) {
        await navigator.share(payload);
        toast.success('Shared');
        return;
      }
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
      toast.message('Share not supported here — downloaded instead');
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('Reseller share failed', err);
      toast.error('Share failed');
    }
  };

  const handleCopyMedia = async (m) => {
    if (!navigator.clipboard || !window.ClipboardItem) {
      toast.error('Copy not supported on this browser — try Share');
      return;
    }
    try {
      const blob = await resellerAPI.fetchMediaBlob(productId, m.id);
      // Generated images are already PNG, so no canvas conversion needed.
      const item = new window.ClipboardItem({ 'image/png': blob });
      await navigator.clipboard.write([item]);
      toast.success('Image copied — paste in any chat');
    } catch (err) {
      console.error('Reseller copy failed', err);
      toast.error('Copy failed');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
      </div>
    );
  }

  const heroExists = (pack.media || []).some((m) => m.status === 'ready');

  return (
    <div className="space-y-6 pb-8" data-testid="reseller-panel">
      {/* ===== Title section ===== */}
      <section className="bg-white border border-zinc-200 rounded-xl p-5">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div>
            <h2 className="text-sm font-semibold text-zinc-900">Title</h2>
            <p className="text-[11px] text-zinc-500">Same as the Etsy listing title — edit on the Etsy tab.</p>
          </div>
          <button
            onClick={() => handleCopy(pack.best_title, 'title', 'Title copied')}
            className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900 border border-zinc-200 hover:border-zinc-400 rounded-md px-2.5 py-1.5"
            data-testid="reseller-copy-title"
          >
            {copyKey === 'title' ? <><Check size={12} className="text-emerald-600" /> Copied</> : <><Copy size={12} /> Copy</>}
          </button>
        </div>
        <p className="text-[13px] text-zinc-800 font-medium leading-snug" data-testid="reseller-title-text">
          {pack.best_title || <span className="text-zinc-400 italic">Generate the Etsy title first.</span>}
        </p>
      </section>

      {/* ===== Description section ===== */}
      <section className="bg-white border border-zinc-200 rounded-xl p-5">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="flex-1">
            <h2 className="text-sm font-semibold text-zinc-900 inline-flex items-center gap-1.5">
              <Sparkles size={13} className="text-rose-500" />
              Product description
            </h2>
            <p className="text-[11px] text-zinc-500">
              Facebook-Marketplace style spec sheet — paste into reseller chats. Always ends with the "willing to meet at any jeweler" footer.
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            {pack.description && !editingDesc && (
              <>
                <button
                  onClick={() => handleCopy(pack.description, 'desc', 'Description copied')}
                  className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900 border border-zinc-200 hover:border-zinc-400 rounded-md px-2.5 py-1.5"
                  data-testid="reseller-copy-description"
                >
                  {copyKey === 'desc' ? <><Check size={12} className="text-emerald-600" /> Copied</> : <><Copy size={12} /> Copy</>}
                </button>
                <button
                  onClick={() => { setDescDraft(pack.description); setEditingDesc(true); }}
                  className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900 border border-zinc-200 hover:border-zinc-400 rounded-md px-2.5 py-1.5"
                  data-testid="reseller-edit-description"
                >
                  <Edit3 size={12} /> Edit
                </button>
              </>
            )}
            <Button
              onClick={handleGenerateDescription}
              disabled={generatingDesc}
              className="bg-rose-600 hover:bg-rose-700 text-white h-8 px-3 text-xs"
              data-testid="reseller-generate-description-btn"
            >
              {generatingDesc ? (
                <><Loader2 size={12} className="animate-spin mr-1.5" /> Generating…</>
              ) : (
                <><RefreshCw size={12} className="mr-1.5" /> {pack.description ? 'Regenerate' : 'Generate'}</>
              )}
            </Button>
          </div>
        </div>

        {editingDesc ? (
          <div className="space-y-2">
            <textarea
              value={descDraft}
              onChange={(e) => setDescDraft(e.target.value)}
              rows={18}
              className="w-full text-[13px] font-mono bg-zinc-50 border border-zinc-200 rounded-md p-3 leading-relaxed text-zinc-800 focus:outline-none focus:ring-2 focus:ring-rose-200"
              data-testid="reseller-description-textarea"
            />
            <div className="flex items-center gap-2">
              <Button onClick={handleSaveDescription} disabled={savingDesc} size="sm" className="bg-zinc-900 hover:bg-zinc-800 text-white h-8">
                {savingDesc ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                <span className="ml-1.5">Save</span>
              </Button>
              <Button onClick={() => setEditingDesc(false)} size="sm" variant="outline" className="h-8">
                <X size={12} /> <span className="ml-1.5">Cancel</span>
              </Button>
            </div>
          </div>
        ) : pack.description ? (
          <pre className="bg-zinc-50 border border-zinc-200 rounded-md p-3 text-[13px] text-zinc-800 leading-relaxed whitespace-pre-wrap font-mono" data-testid="reseller-description-display">
            {pack.description}
          </pre>
        ) : (
          <div className="border border-dashed border-zinc-200 rounded-md p-6 text-center text-xs text-zinc-400">
            No description yet. Click <strong className="text-zinc-700">Generate</strong> to build the Facebook-Marketplace spec sheet from your Etsy listing.
          </div>
        )}
      </section>

      {/* ===== Reseller media section ===== */}
      <section className="bg-white border border-zinc-200 rounded-xl p-5">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="flex-1">
            <h2 className="text-sm font-semibold text-zinc-900 inline-flex items-center gap-1.5">
              <ImageIcon size={13} className="text-rose-500" />
              Reseller media
            </h2>
            <p className="text-[11px] text-zinc-500 leading-relaxed">
              gpt-image-2 renders your studio photo in a raw, "real" Facebook-Marketplace style. Start with a <strong>Hero</strong> (ring in box) and add <strong>Continuation</strong> shots from other angles.
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              onClick={() => openPicker('hero')}
              disabled={!productPhotos.length || submittingGen}
              className="bg-rose-600 hover:bg-rose-700 text-white h-8 px-3 text-xs"
              data-testid="reseller-generate-hero-btn"
            >
              <Wand2 size={12} className="mr-1.5" /> Generate Hero
            </Button>
            <Button
              onClick={() => openPicker('continuation')}
              disabled={!heroExists || !productPhotos.length || submittingGen}
              variant="outline"
              className="h-8 px-3 text-xs"
              title={heroExists ? '' : 'Generate a Hero image first'}
              data-testid="reseller-generate-continuation-btn"
            >
              <ImagePlus size={12} className="mr-1.5" /> Continuation
            </Button>
          </div>
        </div>

        {(pack.media || []).length === 0 ? (
          <div className="border border-dashed border-zinc-200 rounded-md p-6 text-center text-xs text-zinc-400">
            {productPhotos.length === 0
              ? 'Upload product photos on the Etsy tab first, then come back to generate reseller images.'
              : 'Click Generate Hero to create your first FB-Marketplace-style image.'}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="reseller-media-grid">
            {(pack.media || []).map((m) => (
              <ResellerMediaTile
                key={m.id}
                media={m}
                onPreview={setPreviewMedia}
                onShare={handleShareMedia}
                onCopy={handleCopyMedia}
                onDelete={handleDeleteMedia}
                productPhotos={productPhotos}
              />
            ))}
          </div>
        )}
      </section>

      {/* ===== Source photo picker dialog ===== */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="sm:max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle>
              {pickerKind === 'hero' ? 'Pick the product photo for the hero (ring-in-box)' : 'Pick the product photo for the next angle'}
            </DialogTitle>
          </DialogHeader>
          {productPhotos.length === 0 ? (
            <div className="p-6 text-center text-sm text-zinc-500">No product photos yet — upload some on the Etsy tab first.</div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 max-h-[60vh] overflow-y-auto p-1">
              {productPhotos.map((m) => (
                <button
                  key={m.id}
                  onClick={() => handlePickSource(m.id)}
                  disabled={submittingGen}
                  className="group relative aspect-square rounded-md overflow-hidden border border-zinc-200 hover:border-rose-400 hover:ring-2 hover:ring-rose-200 transition-all disabled:opacity-40"
                  data-testid={`pick-source-${m.id}`}
                >
                  {m.view_url ? (
                    <img src={m.view_url} alt={m.display_name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-zinc-100 flex items-center justify-center text-zinc-400">
                      <ImageIcon size={20} />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-rose-600/0 group-hover:bg-rose-600/20 transition-colors flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-[11px] font-semibold bg-rose-600 px-2 py-1 rounded">
                      {submittingGen ? <Loader2 size={11} className="animate-spin" /> : 'Use this'}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== Preview modal ===== */}
      <Dialog open={!!previewMedia} onOpenChange={() => setPreviewMedia(null)}>
        <DialogContent className="sm:max-w-3xl bg-zinc-900 text-white border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white truncate">{previewMedia?.display_name}</DialogTitle>
          </DialogHeader>
          {previewMedia?.view_url && (
            <img src={previewMedia.view_url} alt={previewMedia.display_name} className="w-full rounded-md max-h-[70vh] object-contain" />
          )}
          {previewMedia?.view_url && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <button
                onClick={() => handleShareMedia(previewMedia)}
                className="inline-flex items-center justify-center gap-1.5 bg-rose-600 hover:bg-rose-700 text-white text-sm py-2 px-4 rounded-md"
              >
                <Share2 size={14} /> Share / Save to Photos
              </button>
              <button
                onClick={() => handleCopyMedia(previewMedia)}
                className="inline-flex items-center justify-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-sm py-2 px-4 rounded-md border border-white/20"
              >
                <ClipboardCopy size={14} /> Copy image
              </button>
              <a
                href={previewMedia.download_url}
                download={previewMedia.display_name}
                className="inline-flex items-center justify-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-sm py-2 px-4 rounded-md border border-white/20"
              >
                Download
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


function ResellerMediaTile({ media, onPreview, onShare, onCopy, onDelete, productPhotos }) {
  const isPending = media.status === 'pending';
  const isFailed = media.status === 'failed';
  const isReady = media.status === 'ready';
  const sourcePhoto = productPhotos.find((p) => p.id === media.source_media_id);
  return (
    <div
      className="group rounded-lg border border-zinc-200 overflow-hidden bg-zinc-50 hover:border-zinc-300 transition-colors flex flex-col"
      data-testid={`reseller-media-${media.id}`}
    >
      <div className="aspect-square relative bg-zinc-900/5 flex items-center justify-center">
        {isPending ? (
          <div className="flex flex-col items-center gap-2 text-zinc-500 px-4 text-center">
            <Loader2 size={20} className="animate-spin" />
            <span className="text-[11px]">Generating with gpt-image-2…</span>
            <span className="text-[10px] text-zinc-400">~2 min</span>
          </div>
        ) : isFailed ? (
          <div className="flex flex-col items-center gap-2 text-red-600 px-4 text-center">
            <AlertCircle size={20} />
            <span className="text-[11px] font-semibold">Failed</span>
            <span className="text-[10px] text-red-500 line-clamp-3">{media.error || 'Unknown error'}</span>
          </div>
        ) : media.view_url ? (
          <img
            src={media.view_url}
            alt={media.display_name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <ImageIcon size={24} className="text-zinc-400" />
        )}

        {/* Action overlay — always visible on mobile, hover-only on desktop */}
        {isReady && (
          <div className="absolute inset-0 bg-black/40 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1.5 p-1">
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => onShare(media)}
                className="bg-rose-600 text-white rounded-full p-1.5 hover:scale-105 transition-transform shadow-sm"
                title="Share"
                data-testid={`reseller-share-${media.id}`}
              >
                <Share2 size={13} />
              </button>
              <button
                onClick={() => onCopy(media)}
                className="bg-white text-zinc-900 rounded-full p-1.5 hover:scale-105 transition-transform shadow-sm"
                title="Copy image"
                data-testid={`reseller-copy-${media.id}`}
              >
                <ClipboardCopy size={13} />
              </button>
              <button
                onClick={() => onPreview(media)}
                className="bg-white text-zinc-900 rounded-full p-1.5 hover:scale-105 transition-transform shadow-sm"
                title="Preview"
                data-testid={`reseller-preview-${media.id}`}
              >
                <Eye size={13} />
              </button>
              <button
                onClick={() => onDelete(media.id)}
                className="bg-white text-red-600 rounded-full p-1.5 hover:scale-105 transition-transform shadow-sm"
                title="Delete"
                data-testid={`reseller-delete-${media.id}`}
              >
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        )}
        {/* Delete button still visible for pending/failed */}
        {(isPending || isFailed) && (
          <button
            onClick={() => onDelete(media.id)}
            className="absolute top-1.5 right-1.5 bg-white text-red-600 rounded-full p-1.5 hover:scale-105 transition-transform shadow"
            title="Cancel / Delete"
            data-testid={`reseller-delete-${media.id}`}
          >
            <Trash2 size={11} />
          </button>
        )}
      </div>
      <div className="px-2.5 py-2 bg-white flex-1 flex flex-col gap-1">
        <div className="flex items-center justify-between gap-2">
          <span className={`text-[9px] px-1.5 py-0.5 rounded-full border font-semibold uppercase tracking-wider ${STATUS_COLOR[media.status] || 'bg-zinc-50 text-zinc-600 border-zinc-200'}`}>
            {media.generation_kind === 'hero' ? 'Hero' : 'Continuation'}
          </span>
          <span className="text-[9px] text-zinc-400">
            {media.file_size ? `${(media.file_size / 1024 / 1024).toFixed(2)} MB` : ''}
          </span>
        </div>
        {sourcePhoto && (
          <div className="text-[10px] text-zinc-500 truncate" title={sourcePhoto.display_name}>
            from: <span className="font-mono text-zinc-700">{sourcePhoto.display_name || sourcePhoto.original_name}</span>
          </div>
        )}
      </div>
    </div>
  );
}
